import { FC, useRef } from "react";
import { createStyles } from "@mantine/emotion";
import { Grid, Stepper, Space } from "@mantine/core";
import {
  GetOrderByUniqueAttributeQuery,
  GetRemovableItemChoicesQuery,
  GetUserProductPreferencesQuery,
} from "/@/generated/graphql";
import {
  CreateRemovableItemFullDentureImprintChoice,
  CreateRemovableItemFullDentureOrderTypeChoice,
  CreateRemovableItemFullDentureMaterial,
  CreateRemovableItemFullDentureAesthetics,
  CreateRemovableItemFullDentureInfo,
  getStepperLabels,
  ImprintChoiceRef,
  OrderTypeChoiceRef,
  MaterialRef,
  AestheticsRef,
} from "@jasper/shared";
import { CreateProductSummarySideBar } from "/@/pages/products/create/CreateProductSummarySideBar";
import { useCreateOrderStore } from "/@/shared/stores/orders/create-store.store";
import { CreateOrderReview } from "/@/pages/orders/CreateOrderReview";
import { CreateRemovableItemFullDentureStep } from "/@/shared/types/orders/createOrder.types";
import { useAuthContext } from "../../../../shared/contexts/AuthContext";

type CreateRemovableItemFullDentureProps = {
  product: NonNullable<
    GetOrderByUniqueAttributeQuery["getOrderByUniqueAttribute"]["products"]
  >[0];
  patient: GetOrderByUniqueAttributeQuery["getOrderByUniqueAttribute"]["patient"];
  userProductPreferences: GetUserProductPreferencesQuery;
  removableItemChoicesData: GetRemovableItemChoicesQuery;
  order: GetOrderByUniqueAttributeQuery["getOrderByUniqueAttribute"];
  setIsLoading: (isLoading: boolean) => void;
};

export const CreateRemovableItemFullDenture: FC<
  CreateRemovableItemFullDentureProps
> = ({ patient, product, removableItemChoicesData, order, setIsLoading }) => {
  const { user } = useAuthContext();
  const [createProductStep, setCreateProductStep] = useCreateOrderStore(
    state => [state.createProductStep, state.setCreateProductStep]
  );

  const { classes } = useStyles();

  const newRemovableItem = product.removableItem?.[0];

  const orderType = newRemovableItem?.fullDentureOrderType;

  const stepperLabels = getStepperLabels(orderType ?? null);

  const imprintChoiceRef = useRef<ImprintChoiceRef>(null);
  const orderTypeChoiceRef = useRef<OrderTypeChoiceRef>(null);
  const materialRef = useRef<MaterialRef>(null);
  const aestheticsRef = useRef<AestheticsRef>(null);

  const handleStepChange = async (newStep: number) => {
    switch (createProductStep) {
      case CreateRemovableItemFullDentureStep.ImprintChoice:
        await imprintChoiceRef.current?.saveData();
        break;
      case CreateRemovableItemFullDentureStep.OrderType:
        await orderTypeChoiceRef.current?.saveData();
        break;
      case CreateRemovableItemFullDentureStep.DentureDetails:
        await materialRef.current?.saveData();
        break;
      case CreateRemovableItemFullDentureStep.Aesthetics:
        await aestheticsRef.current?.saveData();
        break;
    }
    setCreateProductStep(newStep);
  };

  const dataContent = () => {
    switch (createProductStep) {
      case CreateRemovableItemFullDentureStep.ImprintChoice:
        return (
          <CreateRemovableItemFullDentureImprintChoice
            ref={imprintChoiceRef}
            product={product}
          />
        );
      case CreateRemovableItemFullDentureStep.OrderType:
        return (
          <CreateRemovableItemFullDentureOrderTypeChoice
            ref={orderTypeChoiceRef}
            product={product}
          />
        );
      case CreateRemovableItemFullDentureStep.DentureDetails:
        return (
          <CreateRemovableItemFullDentureMaterial
            ref={materialRef}
            key="material-step"
            product={product}
            removableItemChoicesData={removableItemChoicesData}
          />
        );
      case CreateRemovableItemFullDentureStep.Aesthetics:
        return (
          <CreateRemovableItemFullDentureAesthetics
            ref={aestheticsRef}
            product={product}
            removableItemChoicesData={removableItemChoicesData}
          />
        );
      case CreateRemovableItemFullDentureStep.Review:
        return (
          <CreateOrderReview
            product={product}
            order={order}
            refetchOrder={() => {}}
            setIsLoading={setIsLoading}
            onValidationComplete={() => {
              setCreateProductStep(CreateRemovableItemFullDentureStep.Info);
            }}
          />
        );
      case CreateRemovableItemFullDentureStep.Info:
        return (
          <CreateRemovableItemFullDentureInfo
            product={product}
            orderId={order.id}
            pickupCarrier={user?.userGroup?.pickupCarrier}
          />
        );
      default:
        return <div>ERROR create removable item full denture</div>;
    }
  };

  return (
    <Grid columns={36} gap={0} style={{ height: "100%" }}>
      <Grid.Col
        span={
          createProductStep < CreateRemovableItemFullDentureStep.Review
            ? 26
            : 36
        }
        style={theme => ({
          padding: theme.spacing.lg,
        })}
      >
        <Stepper
          active={createProductStep}
          onStepClick={handleStepChange}
          classNames={{
            step: classes.stepper,
            stepBody: classes.stepBody,
          }}
        >
          {stepperLabels.map((label, index) => (
            <Stepper.Step key={index} label={label} />
          ))}
        </Stepper>
        <Space h="xl" />
        {dataContent()}
      </Grid.Col>
      {createProductStep < CreateRemovableItemFullDentureStep.Review && (
        <Grid.Col
          span={10}
          style={theme => ({
            padding: theme.spacing.sm,
            backgroundColor: theme.colors.primaryLight[9],
          })}
        >
          <CreateProductSummarySideBar
            product={product}
            patient={patient}
            removableItemChoicesData={removableItemChoicesData}
          />
        </Grid.Col>
      )}
    </Grid>
  );
};

const useStyles = createStyles(theme => ({
  stepper: {
    flexDirection: "column",
  },
  stepBody: {
    marginLeft: 0,
    marginTop: theme.spacing.xs,
    alignItems: "center",
  },
}));
